import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import DomioLogo from '../components/common/DomioLogo';
import { authAPI } from '../lib/api';
import { useI18n } from '../i18n';

export default function VerifyEmailPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState('loading');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('missing');
      return;
    }
    const run = async () => {
      try {
        const res = await authAPI.verifyEmail(token);
        setEmail(res.data?.email || '');
        setStatus('success');
      } catch {
        setStatus('error');
      }
    };
    run();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-6">
      <div className="mb-8">
        <DomioLogo className="h-10" />
      </div>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-domio-600 mx-auto mb-4" />
            <p className="text-slate-600">{t('auth.verifyEmailLoading')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="h-12 w-12 text-domio-green mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">{t('auth.verifyEmailSuccessTitle')}</h1>
            <p className="text-slate-600 mb-6">
              {email ? t('auth.verifyEmailSuccessBody', { email }) : t('auth.verifyEmailSuccessBodyGeneric')}
            </p>
            <Button asChild className="w-full bg-domio-green hover:bg-domio-green-dark">
              <Link to="/login">{t('auth.signIn')}</Link>
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">{t('auth.verifyEmailErrorTitle')}</h1>
            <p className="text-slate-600 mb-6">{t('auth.verifyEmailErrorBody')}</p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t('auth.signIn')}</Link>
            </Button>
          </>
        )}
        {status === 'missing' && (
          <>
            <XCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="text-slate-600 mb-6">{t('auth.verifyEmailMissing')}</p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/register">{t('auth.createAccount')}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
